import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LocateFixed, Building2 } from "lucide-react";

export default function RegistrarPlaza() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [f, setF] = useState({
    plaza_nombre: "", plaza_direccion: "", lat: 0, lng: 0,
    radio_entrega_km: 5, costo_envio: 3.99,
    admin_nombre: "", admin_telefono: "", admin_email: "", admin_password: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  const geo = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(pos => {
      set("lat", pos.coords.latitude); set("lng", pos.coords.longitude);
      toast.success("Ubicación GPS capturada");
    }, () => toast.error("No se pudo capturar ubicación"));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/plazas/register", f);
      toast.success("¡Plaza registrada!");
      await refresh();
      nav("/admin");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="overline text-zinc-500">White-label</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-1">Registra tu Centro Comercial</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-md">Tu propia app hiperlocal en minutos. Cada plaza tiene su branding, colores, logo y equipo de repartidores.</p>

        <form onSubmit={submit} className="mt-8 space-y-5 bg-white border border-zinc-200 rounded-sm p-6">
          <div>
            <div className="overline text-zinc-500 mb-2 flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> Datos de la plaza</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nombre del centro comercial</Label>
                <Input data-testid="rp-nombre" value={f.plaza_nombre} onChange={e => set("plaza_nombre", e.target.value)} required className="mt-1 rounded-sm h-11" />
              </div>
              <div className="col-span-2">
                <Label>Dirección</Label>
                <Input data-testid="rp-direccion" value={f.plaza_direccion} onChange={e => set("plaza_direccion", e.target.value)} required className="mt-1 rounded-sm h-11" />
              </div>
              <div>
                <Label>Latitud</Label>
                <Input data-testid="rp-lat" type="number" step="any" value={f.lat} onChange={e => set("lat", parseFloat(e.target.value) || 0)} className="mt-1 rounded-sm h-11 font-mono" />
              </div>
              <div>
                <Label>Longitud</Label>
                <Input data-testid="rp-lng" type="number" step="any" value={f.lng} onChange={e => set("lng", parseFloat(e.target.value) || 0)} className="mt-1 rounded-sm h-11 font-mono" />
              </div>
              <div>
                <Label>Radio entrega (km)</Label>
                <Input type="number" step="0.5" value={f.radio_entrega_km} onChange={e => set("radio_entrega_km", parseFloat(e.target.value) || 5)} className="mt-1 rounded-sm h-11" />
              </div>
              <div>
                <Label>Costo envío ($)</Label>
                <Input type="number" step="0.01" value={f.costo_envio} onChange={e => set("costo_envio", parseFloat(e.target.value) || 0)} className="mt-1 rounded-sm h-11" />
              </div>
            </div>
            <Button type="button" data-testid="rp-geo" onClick={geo} variant="outline" size="sm" className="mt-3 rounded-sm">
              <LocateFixed className="w-3.5 h-3.5 mr-1" /> Usar mi ubicación GPS
            </Button>
          </div>

          <div className="pt-4 border-t border-zinc-100">
            <div className="overline text-zinc-500 mb-2">Tu cuenta de admin</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nombre</Label>
                <Input data-testid="rp-admin-nombre" value={f.admin_nombre} onChange={e => set("admin_nombre", e.target.value)} required className="mt-1 rounded-sm h-11" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input data-testid="rp-admin-tel" value={f.admin_telefono} onChange={e => set("admin_telefono", e.target.value)} className="mt-1 rounded-sm h-11" />
              </div>
              <div className="col-span-2">
                <Label>Email</Label>
                <Input data-testid="rp-admin-email" type="email" value={f.admin_email} onChange={e => set("admin_email", e.target.value)} required className="mt-1 rounded-sm h-11" />
              </div>
              <div className="col-span-2">
                <Label>Contraseña</Label>
                <Input data-testid="rp-admin-password" type="password" minLength={6} value={f.admin_password} onChange={e => set("admin_password", e.target.value)} required className="mt-1 rounded-sm h-11" />
              </div>
            </div>
          </div>

          <Button type="submit" data-testid="rp-submit" disabled={loading} className="w-full h-12 rounded-sm bg-[#FF3366] hover:bg-[#E62958]">
            {loading ? "Registrando…" : "Registrar plaza y crear mi cuenta"}
          </Button>
          <div className="text-center text-xs text-zinc-500">
            ¿Ya tienes una plaza registrada? <Link to="/login" className="text-[#FF3366] hover:underline">Entrar</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
